import { ComponentPropsWithoutRef, useState } from "react";

import dayjs from "dayjs";

import AlertModal from "@/components/Modal/AlertModal";
import useModal from "@/hooks/useModal";
import { ReservationResponse } from "@/types/reservation";
import { cn } from "@/utils/cn";
import { checkStatus } from "@/utils/reservation";

const hours = Array.from({ length: 18 }, (_, index) => 6 + index);

const formatTime = (t: number) => {
  const h = Math.floor(t);
  const m = t % 1 === 0.5 ? "30" : "00";
  return `${String(h).padStart(2, "0")}:${m}`;
};

const alertMessage = {
  irrevocable: "해제할 수 없는 시간대입니다.",
  over: "2시간까지 선택 가능합니다.",
  disconnected: "연속된 시간대만 선택 가능합니다.",
};

const TimeTableHead = () => (
  <div className="flex h-full w-10 shrink-0 flex-col">
    <div className="flex flex-1 items-center justify-center rounded-tl-lg bg-gray-100 text-xs font-semibold text-gray-500">
      시
    </div>
    <div className="flex flex-1 items-center justify-center rounded-bl-lg border-t border-gray-200 bg-gray-50 text-xs font-medium text-gray-400">
      분
    </div>
  </div>
);

interface TimeStatus {
  isPast: boolean; // 지난 시간대
  isReserved: boolean; // 예약 승인
  isWaiting: boolean; // 예약 대기
  isSelected: boolean; // 선택값
}

interface TimeButtonProps extends ComponentPropsWithoutRef<"button"> {
  timeStatus: TimeStatus;
}

const TimeButton = ({ timeStatus, ...props }: TimeButtonProps) => {
  const { isPast, isReserved, isSelected, isWaiting } = timeStatus;

  return (
    <button
      type="button"
      className={cn(
        "h-full flex-1 border-r border-gray-200 transition-all duration-150 last:border-r-0",
        isPast && "cursor-not-allowed bg-gray-100",
        isReserved && "cursor-not-allowed bg-red-100",
        isWaiting && "cursor-not-allowed bg-amber-100",
        !isPast && !isReserved && !isWaiting && !isSelected && "bg-white hover:bg-primary/10",
        isSelected && "bg-primary",
      )}
      disabled={isPast || isReserved || isWaiting}
      {...props}
    />
  );
};
interface TimeTablePickerProps {
  selectedDate: Date;
  reservationList: ReservationResponse[];
  selectedTimes: number[];
  onChange: (newSelectedTimes: number[]) => void;
  showBadge?: boolean;
}

const TimeTablePicker = ({
  selectedDate,
  reservationList,
  selectedTimes,
  onChange,
  showBadge = true,
}: TimeTablePickerProps) => {
  const [startTime, endTime] = [
    Math.min(...selectedTimes),
    Math.max(...selectedTimes),
  ];
  const [alertType, setAlertType] = useState<
    "irrevocable" | "over" | "disconnected"
  >("disconnected");
  const alertModal = useModal();

  const handleClickTime = (selectedTime: number) => {
    const isSelectedNone = selectedTimes.length === 0;
    const isIrrevocable = startTime < selectedTime && selectedTime < endTime;
    const isSelectable =
      startTime - selectedTime === 0.5 || selectedTime - endTime === 0.5;
    const isSelected = selectedTimes.includes(selectedTime);
    const isOverTwoHours = selectedTimes.length === 4;

    if (isSelectedNone) {
      onChange([selectedTime]);
      return;
    }

    if (isIrrevocable) {
      setAlertType("irrevocable");
      alertModal.onOpen();
      return;
    }

    if (isSelected) {
      onChange(selectedTimes.filter(time => time !== selectedTime));
      return;
    }

    if (isOverTwoHours) {
      setAlertType("over");
      alertModal.onOpen();
      return;
    }

    if (isSelectable) {
      onChange([...selectedTimes, selectedTime]);
      return;
    }

    alertModal.onOpen();
    setAlertType("disconnected");
  };

  const getTimeStatus = (hour: number, isSecondHalfHour = false) => {
    const minute = isSecondHalfHour ? 30 : 0;
    const timeToNumber = isSecondHalfHour ? hour + 0.5 : hour;
    const timeToDayjs = dayjs(selectedDate)
      .set("hour", hour)
      .set("minute", minute);

    return {
      isPast: timeToDayjs.isBefore(dayjs(), "minute"),
      isReserved: checkStatus({
        time: timeToNumber,
        reservationList,
        status: "isReserved",
      }),
      isWaiting: checkStatus({
        time: timeToNumber,
        reservationList,
        status: "isWaiting",
      }),
      isSelected: startTime <= timeToNumber && endTime >= timeToNumber,
    };
  };

  const hasSelection = selectedTimes.length > 0;
  const displayStart = hasSelection ? formatTime(startTime) : "";
  const displayEnd = hasSelection ? formatTime(endTime + 0.5) : "";

  return (
    <>
      {showBadge && hasSelection && (
        <div className="mb-1 flex justify-end lg:my-2">
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            {displayStart} ~ {displayEnd}
          </span>
        </div>
      )}
      <div className="flex h-[4.5rem] w-full min-w-max overflow-hidden rounded-lg border border-gray-200 shadow-sm lg:min-w-[544px]">
        <TimeTableHead />

        {hours.map((hour, idx) => {
          const firstHalfHourStatus = getTimeStatus(hour);
          const secondHalfHourStatus = getTimeStatus(hour, true);
          const isLast = idx === hours.length - 1;

          return (
            <div
              key={hour}
              className={cn(
                "flex h-full w-[4.5rem] flex-col lg:flex-1",
                !isLast && "border-r border-gray-200",
              )}
            >
              <div className="flex flex-1 items-center justify-center bg-gray-100 text-xs font-semibold text-gray-600">
                {hour}
              </div>
              <div className="flex flex-1 border-t border-gray-200">
                <TimeButton
                  timeStatus={firstHalfHourStatus}
                  onClick={() => handleClickTime(hour)}
                />
                <TimeButton
                  timeStatus={secondHalfHourStatus}
                  onClick={() => handleClickTime(hour + 0.5)}
                />
              </div>
            </div>
          );
        })}
      </div>
      {alertModal.isOpen && (
        <AlertModal onClose={alertModal.onClose}>
          {alertMessage[alertType]}
        </AlertModal>
      )}
    </>
  );
};

export default TimeTablePicker;
